import React from "react";
import { useSearchParams } from "react-router-dom";
import { useMPDDataPanel } from "../../components/widgets/MyProviderData/hooks/useMPDDataPanel";
import BillableHoursCard from "../../components/widgets/MyProviderData/BillableHoursCard";
import GridCard from "../../components/widgets/MyProviderData/GridCard";

const MPDDataPanel: React.FC = () => {
  const [searchParams] = useSearchParams();
  const username = searchParams.get("username") ?? undefined;
  const eventDate = searchParams.get("eventDate") ?? undefined;

  const { data, loading, error } = useMPDDataPanel(username, eventDate);

  // Roles flagged Leadership on the PBS Roles List only accrue billed + billable credit
  // toward their quarterly total, so the other columns are hidden rather than shown as
  // unused numbers. The flag comes from the API, not a list of role names.
  const leadership = data?.isLeadership === true;

  if (!username || !eventDate) {
    return (
      <div className="p-4 text-gray-500 dark:text-gray-400">
        Missing required parameters (username, eventDate).
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-600 dark:text-red-400">
        {error}
      </div>
    );
  }

  const formattedDate = new Date(eventDate + "T00:00:00").toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="grid grid-cols-12 gap-4 p-4 items-start">

      {/* Snapshot banner */}
      <div className="col-span-12 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-md px-4 py-2 text-sm text-amber-800 dark:text-amber-300">
        Read-only snapshot &mdash; values reflect data as of{" "}
        <strong>{formattedDate}</strong>. These figures are not recalculated.
      </div>

      {/* Left column: graphs */}
      <div className="col-span-12 lg:col-span-8 flex flex-col gap-4">
        <BillableHoursCard
          title="Billable Hours per Week"
          data={data?.billableGraph ?? null}
          loading={loading}
          description="Weekly billable performance as of meeting date"
        />
        <BillableHoursCard
          title="Session Note Latency per Week"
          data={data?.latencyGraph ?? null}
          loading={loading}
          description="Average days between service date and note creation"
        />
      </div>

      {/* Right column: scalar metrics */}
      <div className="col-span-12 lg:col-span-4 flex flex-col gap-4">
        {(() => {
          const totalDays = data?.totalDaysInQuarter ?? 0;
          const daysLeft  = data?.daysLeftInQuarter  ?? 0;
          const percentComplete = totalDays > 0 ? (totalDays - daysLeft) / totalDays : 0;
          return (
            <div className="max-w-xs p-4 rounded-md shadow bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700">
              <h3 className="font-semibold mb-2 text-gray-900 dark:text-gray-100">
                Quarter Count
              </h3>
              <hr className="my-2 border-gray-200 dark:border-gray-700" />
              {loading ? (
                <div className="mt-4">
                  <div className="animate-spin inline-block w-6 h-6 border-4 border-blue-500 dark:border-blue-400 border-t-transparent rounded-full" />
                  <p className="mt-2 text-gray-600 dark:text-gray-400">Loading Quarter Counter...</p>
                </div>
              ) : (
                <>
                  <p className="text-gray-800 dark:text-gray-200">
                    Days left in quarter: {daysLeft}
                  </p>
                  <p className="text-gray-700 dark:text-gray-300">
                    Total days in quarter: {totalDays}
                  </p>
                  <div className="w-full h-4 bg-gray-200 dark:bg-gray-700 rounded mt-2 overflow-hidden">
                    <div
                      className="h-4 bg-blue-500 dark:bg-blue-400 rounded"
                      style={{ width: `${(percentComplete * 100).toFixed(0)}%` }}
                    />
                  </div>
                </>
              )}
            </div>
          );
        })()}
        <GridCard
          title="Hours Progress"
          headers={["Prorated Required", "Total Hours", "Pace %"]}
          data={data?.hoursProgress ? [data.hoursProgress] : []}
          loading={loading}
        />
        <GridCard
          title="Current Weekly Average"
          headers={["Weekly Required", "Weekly Avg Billed"]}
          data={data?.weeklyHours ? [data.weeklyHours] : []}
          loading={loading}
        />
        <GridCard
          title="Used Hours to Date"
          headers={
            leadership
              ? ["Billable Credit"]
              : ["Meeting", "Holiday", "PTO/Sick/FH", "Billable Credit"]
          }
          data={
            data?.usedHours
              ? [leadership ? [data.usedHours[3]] : data.usedHours]
              : []
          }
          loading={loading}
        />
      </div>

    </div>
  );
};

export default MPDDataPanel;
